import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import kuromoji from 'kuromoji';
import { furiganaAria } from '../utils/furiganaAria';
import { setFuriganaTokenizer, isFuriganaReady } from '../utils/furigana';

// Global furigana (ルビ) on/off, persisted client-side. Kept OUT of the
// server-synced `settings` object on purpose: it is a display-only reading aid,
// so nothing new is sent to the backend (no telemetry, no participant data).
//
// DEFAULT ON (2026-07-17 requirement): every participant-facing kanji must carry
// a reading, so the aid is present on first use / private mode / when storage is
// unavailable. Only an explicit opt-out ('false') turns it off; that single
// choice is what persists. This default lives in the PROVIDER (readStoredFurigana),
// and App.js wraps the whole app in <FuriganaProvider>, so every real participant
// gets the aid.
//
// READINGS come from a real morphological analyzer (kuromoji), loaded ASYNCHRONOUSLY
// here (~one-time, then browser-cached). Until it is ready, text renders WITHOUT
// readings — we never show an unverified/guessed reading. `ready` flips to true when
// the tokenizer is built, re-rendering consumers so the aid appears.
// v2: the earlier (default-OFF) build persisted v1='false' on mount even for
// participants who never opted out, so under the new default-ON policy those v1 values
// must NOT count as an opt-out. v2 starts fresh (default ON); only an explicit v2
// opt-out persists. A returning participant who genuinely wants it off re-toggles once.
export const FURIGANA_STORAGE_KEY = 'graphTutor::furiganaEnabled::v2';

function readStoredFurigana() {
  try {
    return window.localStorage.getItem(FURIGANA_STORAGE_KEY) !== 'false';
  } catch (e) {
    return true; // storage unavailable → still ON (unconditional reading aid)
  }
}

// Build the kuromoji tokenizer once per page load (module-level promise shared by
// any provider / caller). The dictionary is served from `${PUBLIC_URL}/dict`.
let _buildPromise = null;
function buildFuriganaTokenizer() {
  if (isFuriganaReady()) return Promise.resolve(true);
  if (!_buildPromise) {
    _buildPromise = new Promise((resolve) => {
      try {
        const dicPath = `${process.env.PUBLIC_URL || ''}/dict`;
        kuromoji.builder({ dicPath }).build((err, tokenizer) => {
          if (!err && tokenizer) { setFuriganaTokenizer(tokenizer); resolve(true); }
          // A failed build (offline start / transient CDN error) must NOT stick: drop the cached promise so a
          // later Provider mount or an OFF→ON toggle actually re-invokes the builder instead of reusing the
          // resolved-failed promise and leaving furigana dead until a full reload (Codex R19 #3).
          else { _buildPromise = null; resolve(false); }
        });
      } catch (e) {
        _buildPromise = null; // analyzer unavailable → aid stays off, app unaffected; allow a later retry
        resolve(false);
      }
    });
  }
  return _buildPromise;
}

// The BARE-context fallback (no Provider in the tree) is OFF on purpose: the real
// app is always inside <FuriganaProvider> (App.js) so this value never reaches a
// participant, and defaulting it OFF keeps component unit tests that render without
// a provider free of injected <ruby> in their text/accessible-name assertions.
// Tests that need the aid render inside <FuriganaProvider> or set enabled:true.
export const FuriganaContext = createContext({
  enabled: false,
  ready: false,
  setEnabled: () => {},
  toggle: () => {},
});

export function FuriganaProvider({ children }) {
  const [enabled, setEnabled] = useState(readStoredFurigana);
  const [ready, setReady] = useState(isFuriganaReady);

  useEffect(() => {
    try {
      window.localStorage.setItem(FURIGANA_STORAGE_KEY, enabled ? 'true' : 'false');
    } catch (e) {
      // storage unavailable — the preference still applies for this session.
    }
  }, [enabled]);

  useEffect(() => {
    // Build the ~17MB kuromoji dictionary LAZILY — only while furigana is ENABLED, so an opt-out participant
    // never pays the fetch/inflate/parse cost (all render paths return plain text when disabled). Re-runs when
    // the toggle flips ON; once built the tokenizer is cached module-level, so toggling OFF→ON never rebuilds
    // (Codex R18 #4).
    if (!enabled) return undefined;
    let alive = true;
    if (isFuriganaReady()) { setReady(true); return undefined; }
    buildFuriganaTokenizer().then(() => { if (alive) setReady(isFuriganaReady()); });
    return () => { alive = false; };
  }, [enabled]);

  const toggle = useCallback(() => setEnabled((prev) => !prev), []);
  const value = useMemo(() => ({ enabled, ready, setEnabled, toggle }), [enabled, ready, toggle]);
  return <FuriganaContext.Provider value={value}>{children}</FuriganaContext.Provider>;
}

export function useFurigana() {
  return useContext(FuriganaContext);
}

// Reading aid for plain-text ATTRIBUTE surfaces (title / placeholder / optgroup
// label) that can't host <ruby>. Returns a stable fn that appends parenthetical
// readings when the toggle is ON, and returns the text untouched when OFF. Depends
// on `ready` so callers recompute once the tokenizer is available.
// Usage: const fa = useFuriganaAria(); ... title={fa('削除')}
export function useFuriganaAria() {
  const { enabled, ready } = useFurigana();
  return useCallback((text) => (enabled && ready ? furiganaAria(text) : text), [enabled, ready]);
}
